import { IntPoint } from "./IntPoint";

import Rectangle = createjs.Rectangle;

/**
 * A IntRectangle object is an area defined by its position, as indicated by its top-left corner point (x, y) and by its width and its height.
 * The x, y, width, and height properties of the IntRectangle class are independent of each other; changing the value of one property has no effect on the others. However, the right and bottom properties are integrally related to those four properties. For example, if you change the value of the right property, the value of the width property changes; if you change the bottom property, the value of the height property changes.
 * @author npuschke
 *
 */
export class IntRectangle {
    /**
     * The x coordinate of the top-left corner of the rectangle.
     */
    public x: number;
    /**
     * The y coordinate of the top-left corner of the rectangle.
     */
    public y: number;
    /**
     * The width of the rectangle.
     */
    public width: number;
    /**
     * The height of the rectangle.
     */
    public height: number;

    /**
     * Creates a new IntRectangle object with the top-left corner specified by the x and y parameters and with the specified width and height parameters.
     * If you call this function without parameters, a rectangle with x, y, width, and height properties set to 0 is created.
     *
     * @param x The x coordinate of the top-left corner of the rectangle.
     * @param y The y coordinate of the top-left corner of the rectangle.
     * @param width The width of the rectangle.
     * @param height The height of the rectangle.
     *
     */
    constructor(x: number = 0, y: number = 0, width: number = 0, height: number = 0) {
        this.x = x;
        this.y = y;
        this.width = width;
        this.height = height;
    }

    public static fromRectangle(rect: Rectangle): IntRectangle {
        return new IntRectangle(rect.x, rect.y, rect.width, rect.height);
    }

    public get bottom(): number {
        return this.y + this.height;
    }

    public set bottom(value: number) {
        this.height += (value - this.bottom);
    }

    public get bottomRight(): IntPoint {
        return new IntPoint(this.x + this.width, this.y + this.height);
    }

    public set bottomRight(point: IntPoint) {
        this.right = point.x;
        this.bottom = point.y;
    }

    public get left(): number {
        return this.x;
    }

    public set left(value: number) {
        this.width -= (value - this.x);
        this.x = value;
    }

    public get right(): number {
        return this.x + this.width;
    }

    public set right(value: number) {
        this.width += (value - this.right);
    }

    public get size(): IntPoint {
        return new IntPoint(this.width, this.height);
    }

    public set size(point: IntPoint) {
        this.width = point.x;
        this.height = point.y;
    }

    public get top(): number {
        return this.y;
    }

    public set top(value: number) {
        this.height -= (value - this.y);
        this.y = value;
    }

    public get topLeft(): IntPoint {
        return new IntPoint(this.x, this.y);
    }

    public set topLeft(point: IntPoint) {
        this.left = point.x;
        this.top = point.y;
    }

    public get center(): IntPoint {
        return new IntPoint(this.left + (this.width >> 1), this.top + (this.height >> 1));
    }

    public clone(): IntRectangle {
        return new IntRectangle(this.x, this.y, this.width, this.height);
    }

    public contains(x: number, y: number): boolean {
        return (x >= this.left && x <= this.right && y >= this.top && y <= this.bottom);
    }

    public containsPoint(point: IntPoint): boolean {
        return this.contains(point.x, point.y);
    }

    public containsRect(rect: IntRectangle): boolean {
        return (rect.left >= this.left && rect.right <= this.right && rect.top >= this.top && rect.bottom <= this.bottom);
    }

    public equals(rect: IntRectangle): boolean {
        return (rect.x == this.x && rect.y == this.y && rect.width == this.width && rect.height == this.height);
    }

    public intersection(toIntersect: IntRectangle): IntRectangle {
        let intersectionRect: IntRectangle = new IntRectangle();
        intersectionRect.left = Math.max(this.left, toIntersect.left);
        intersectionRect.right = Math.min(this.right, toIntersect.right);
        intersectionRect.top = Math.max(this.top, toIntersect.top);
        intersectionRect.bottom = Math.min(this.bottom, toIntersect.bottom);

        if (!(intersectionRect.left < intersectionRect.right && intersectionRect.top < intersectionRect.bottom)) {
            intersectionRect.setEmpty();
        }

        return intersectionRect;
    }

    public intersects(toIntersect: IntRectangle): boolean {
        return !this.intersection(toIntersect).isEmpty();
    }

    public isEmpty(): boolean {
        return (this.width == 0 || this.height == 0);
    }

    public offset(dx: number, dy: number): void {
        this.x += dx;
        this.y += dy;
    }

    public offsetPoint(point: IntPoint): void {
        this.offset(point.x, point.y);
    }

    public setEmpty(): void {
        this.x = 0;
        this.y = 0;
        this.width = 0;
        this.height = 0;
    }

    public toString(): string {
        return "IntRect (x:" + this.x + ", y:" + this.y + ", width:" + this.width + ", height:" + this.height + ")";
    }

    public union(toUnion: Rectangle): IntRectangle {
        let unionedRect: IntRectangle = new IntRectangle();

        unionedRect.left = Math.min(this.left, toUnion.left);
        unionedRect.right = Math.max(this.right, toUnion.right);
        unionedRect.top = Math.min(this.top, toUnion.top);
        unionedRect.bottom = Math.max(this.bottom, toUnion.bottom);

        return unionedRect;
    }

}
